import { Box, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CustomChip } from "../customChip/CustomChip";
import { FactorSlider } from "./FactorSlider";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  root: {
    display: "grid",
    gap: theme.analysisLayout.compactGap,
    minWidth: 0,
    padding: theme.analysisLayout.cardPadding,
    borderRadius: "4px",
    backgroundColor: "white",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.analysisLayout.compactGap,
    minWidth: 0,
  },
  headerWithToggle: {
    justifyContent: "space-between",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    gap: "8px",
    minWidth: 0,
  },
  icon: {
    display: "flex",
    alignItems: "center",
    color: theme.semanticColors.text.primary,
    flexShrink: 0,
    fontSize: "30px",
    "& svg": {
      fontSize: "inherit",
    },
  },
  valueRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
});

interface FactorAdjustmentCardProps {
  icon?: ReactNode;
  header: ReactNode;
  min: number;
  max: number;
  value: number;
  step?: number;
  roundValue?: boolean;
  recommendedValue?: number | null;
  valueLabel?: ReactNode;
  unit?: string;
  toggle?: ReactNode;
  formatValue?: (value: number) => string;
  onChange?: (_event: Event, value: number | number[]) => void;
}

export const FactorAdjustmentCard = ({
  icon,
  header,
  min,
  max,
  value,
  step,
  roundValue = false,
  recommendedValue,
  valueLabel,
  unit,
  toggle,
  formatValue,
  onChange,
}: FactorAdjustmentCardProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("IndicativeMarketValuationPanel");
  const formatDisplayValue = (displayValue: number): string => {
    if (formatValue) {
      return formatValue(displayValue);
    }

    return roundValue
      ? Math.round(displayValue).toLocaleString("de-DE")
      : displayValue.toLocaleString("de-DE", { maximumFractionDigits: 3 });
  };
  const formattedRecommendedValue = recommendedValue != null ? formatDisplayValue(recommendedValue) : null;
  const formattedRecommendedValueWithUnit =
    formattedRecommendedValue != null ? `${formattedRecommendedValue}${unit ? ` ${unit}` : ""}` : null;

  return (
    <Box sx={styles.root}>
      <Box sx={{ ...styles.header, ...(toggle ? styles.headerWithToggle : undefined) }}>
        <Box sx={styles.label}>
          {icon ? <Box sx={styles.icon}>{icon}</Box> : null}
          <Typography variant="body1">{header}</Typography>
        </Box>
        {toggle}
      </Box>
      <Box sx={styles.valueRow}>
        <Typography variant="modalHeader">
          {valueLabel ?? formatDisplayValue(value)}
          {unit ? ` ${unit}` : null}
        </Typography>
        {formattedRecommendedValueWithUnit != null ? (
          <CustomChip label={t("recommendedValue", { value: formattedRecommendedValueWithUnit })} chipType="primary" />
        ) : null}
      </Box>
      <FactorSlider
        min={min}
        max={max}
        value={value}
        recommendedValue={recommendedValue}
        step={step}
        roundValue={roundValue}
        showTitle={false}
        formatValue={formatValue}
        onChange={onChange}
      />
    </Box>
  );
};
